import * as React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  CreditCard,
  FlaskConical,
  HelpCircle,
  LogOut,
  Menu,
  ScrollText,
  Settings,
  UserCircle,
  X,
} from 'lucide-react';
import { useAuth } from '@proptr/api-client/react';
import { isTestMode, setTestMode } from '../../lib/testMode.js';
import { cn, initials } from '../../lib/utils.js';
import { Button } from '../ui/button.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu.js';
import { ThemeToggle } from './ThemeToggle.js';

const NAV: { to: string; label: string; highlight?: boolean }[] = [
  { to: '/', label: 'Dashboard' },
  { to: '/agents', label: 'Agents' },
  { to: '/listings', label: 'Listings' },
  { to: '/proptree', label: 'Proptree' },
  { to: '/affiliate', label: 'Affiliate' },
  { to: '/forms', label: 'Forms' },
  { to: '/inquiries', label: 'Inquiries' },
  { to: '/contacts', label: 'Contacts' },
  { to: '/marketing', label: 'Marketing' },
  // Highlighted so it reads as a distinct utility action rather than
  // another section tab.
  { to: '/settings', label: 'CRM settings', highlight: true },
];

// Tabs that only make sense when the brokerage syncs a CRM. Hidden (not
// removed) while enterprise.crmSyncEnabled is false.
const CRM_SYNC_TABS = new Set(['/agents', '/listings', '/proptree']);

export function Topbar() {
  const { user, enterprise, logout } = useAuth();
  const crmSync = enterprise?.crmSyncEnabled !== false;
  const nav = NAV.filter((item) => crmSync || !CRM_SYNC_TABS.has(item.to));
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = React.useState(false);

  async function onLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <>
      {/* Stickiness lives on AppShell's header wrapper (banners + topbar
          stick as one block), not here. */}
      <header className="border-b border-[hsl(var(--hairline))] bg-card">
        <div className="mx-auto flex h-16 w-full max-w-[1400px] items-center gap-3 px-4 md:gap-6 md:px-6">
          {/* Mobile hamburger */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
          >
            <Menu />
          </Button>

          {/* Enterprise logo (falls back to placeholder if none) */}
          <EnterpriseLogo
            logo={enterprise?.logo ?? null}
            name={enterprise?.name ?? null}
          />

          {/* Desktop nav */}
          <nav className="hidden min-w-0 flex-1 items-center md:flex">
            {nav.map((item) =>
              item.highlight ? (
                <NavLink
                  key={item.to}
                  to={item.to}
                  // Exact match only. Without this, NavLink treats the
                  // path as a prefix and `/settings/agency` (agency
                  // settings) and `/settings/profile` both light up the
                  // CRM settings tab.
                  end
                  className={({ isActive }) =>
                    cn(
                      'ml-3 inline-flex h-9 items-center gap-1.5 rounded-md border px-3 font-sans text-[13px] font-medium transition-colors',
                      isActive
                        ? 'border-primary/50 bg-primary/15 text-foreground'
                        : 'border-border bg-muted/60 text-foreground hover:border-foreground/30 hover:bg-muted',
                    )
                  }
                >
                  <Settings className="h-3.5 w-3.5" />
                  {item.label}
                </NavLink>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    cn(
                      'relative flex h-16 items-center px-4 font-sans text-[14px] font-medium transition-colors',
                      isActive
                        ? 'text-foreground'
                        : 'text-muted-foreground hover:text-foreground',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span>{item.label}</span>
                      {isActive && (
                        <span className="absolute inset-x-4 bottom-0 h-[2px] rounded-t-full bg-primary" />
                      )}
                    </>
                  )}
                </NavLink>
              ),
            )}
          </nav>

          {/* Right cluster */}
          <div className="ml-auto flex shrink-0 items-center gap-1">
            <ThemeToggle />
            <div className="mx-1 hidden h-5 w-px bg-[hsl(var(--hairline))] md:block" />
            <ProfileMenu
              user={user}
              onProfile={() => navigate('/settings/profile')}
              onSettings={() => navigate('/settings/agency')}
              onBilling={() => navigate('/billing')}
              onFaq={() => navigate('/faq')}
              onSystemLogs={() => navigate('/system-logs')}
              onLogout={onLogout}
            />
          </div>
        </div>
      </header>

      <MobileNavDrawer
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        logo={enterprise?.logo ?? null}
        enterpriseName={enterprise?.name ?? null}
        items={nav}
      />
    </>
  );
}

function EnterpriseLogo({
  logo,
  name,
}: {
  logo: string | null;
  name: string | null;
}) {
  if (logo) {
    return (
      <div
        className="flex h-9 shrink-0 items-center"
        aria-label={name ?? 'Enterprise logo'}
        title={name ?? undefined}
      >
        <img
          src={logo}
          alt={name ?? 'Enterprise logo'}
          className="max-h-full max-w-[160px] object-contain"
        />
      </div>
    );
  }
  return (
    <div
      className="flex h-9 min-w-[40px] items-center justify-center rounded-md border border-dashed border-border bg-card px-3"
      aria-label="Your logo will appear here"
      title="Your logo will appear here"
    >
      <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        logo
      </span>
    </div>
  );
}

function ProfileMenu({
  user,
  onProfile,
  onSettings,
  onBilling,
  onFaq,
  onSystemLogs,
  onLogout,
}: {
  user: {
    name?: string;
    email?: string;
    role?: string;
    image?: string | null;
  } | null;
  onProfile: () => void;
  onBilling: () => void;
  onSettings: () => void;
  onFaq: () => void;
  onSystemLogs: () => void;
  onLogout: () => void | Promise<void>;
}) {
  const displayName = user?.name ?? user?.email?.split('@')[0] ?? 'User';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-1.5 rounded-md p-1 outline-none ring-offset-background transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Account menu"
        >
          {user?.image ? (
            <img
              src={user.image}
              alt=""
              className="h-8 w-8 shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted font-mono text-[11px] font-bold uppercase">
              {initials(user?.name ?? user?.email ?? 'U')}
            </div>
          )}
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>
          <div className="truncate font-semibold normal-case text-foreground">
            {displayName}
          </div>
          {user?.role && (
            <div className="mt-0.5 text-[11px] font-normal capitalize text-muted-foreground">
              {user.role}
            </div>
          )}
          {user?.email && (
            <div className="truncate text-[11px] font-normal normal-case text-muted-foreground">
              {user.email}
            </div>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onProfile}>
          <UserCircle />
          Profile
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onSettings}>
          <Settings />
          Settings
        </DropdownMenuItem>
        {/* Hidden in test mode, like the dashboard's subscription card. */}
        {!isTestMode() && (
          <DropdownMenuItem onSelect={onBilling}>
            <CreditCard />
            Billing
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={onFaq}>
          <HelpCircle />
          FAQ
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onSystemLogs}>
          <ScrollText />
          System logs
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {/* Demo switch — serves seed fixtures instead of live data and
            shows the test-mode banner. Reload so every page refetches
            through the interceptor. */}
        <DropdownMenuItem
          onSelect={() => {
            setTestMode(!isTestMode());
            window.location.reload();
          }}
        >
          <FlaskConical />
          Testing
          <span
            className={cn(
              'ml-auto rounded-full px-1.5 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-[0.06em]',
              isTestMode()
                ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400'
                : 'bg-muted text-muted-foreground',
            )}
          >
            {isTestMode() ? 'On' : 'Off'}
          </span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            void onLogout();
          }}
        >
          <LogOut />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileNavDrawer({
  open,
  onClose,
  logo,
  enterpriseName,
  items,
}: {
  open: boolean;
  onClose: () => void;
  logo: string | null;
  enterpriseName: string | null;
  items: typeof NAV;
}) {
  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 md:hidden',
        open ? 'pointer-events-auto' : 'pointer-events-none',
      )}
      aria-hidden={!open}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-background/70 backdrop-blur-sm transition-opacity',
          open ? 'opacity-100' : 'opacity-0',
        )}
      />
      {/* Panel */}
      <aside
        className={cn(
          'absolute inset-y-0 left-0 flex w-[280px] flex-col border-r border-[hsl(var(--hairline))] bg-card shadow-pop transition-transform duration-300 ease-out',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 items-center justify-between border-b border-[hsl(var(--hairline))] px-4">
          <EnterpriseLogo logo={logo} name={enterpriseName} />
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close menu">
            <X />
          </Button>
        </div>

        <nav className="flex flex-1 flex-col gap-0.5 p-3">
          <div className="eyebrow px-2 pb-2">Platform</div>
          {items.map((item) =>
            item.highlight ? (
              <NavLink
                key={item.to}
                to={item.to}
                // Exact match — see the desktop nav's note.
                end
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    'mt-1.5 flex items-center gap-2.5 rounded-md border px-3 py-2.5 font-sans text-[14px] font-medium transition-colors',
                    isActive
                      ? 'border-primary/50 bg-primary/15 text-foreground'
                      : 'border-border bg-muted/60 text-foreground hover:bg-muted',
                  )
                }
              >
                <Settings className="h-4 w-4" />
                <span>{item.label}</span>
              </NavLink>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={onClose}
                className={({ isActive }) =>
                  cn(
                    'relative flex items-center gap-2.5 rounded-md px-3 py-2.5 font-sans text-[14px] font-medium transition-colors',
                    isActive
                      ? 'bg-primary/10 text-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute inset-y-2 left-0 w-[2px] rounded-full bg-primary" />
                    )}
                    <span>{item.label}</span>
                  </>
                )}
              </NavLink>
            ),
          )}
        </nav>
      </aside>
    </div>
  );
}
